import React, { useState, useEffect } from 'react';
import Layout from '../layout/Layout';
import { PrivateAxios } from '../../request/axios';
import { toast } from 'react-toastify';
import { FaTrash, FaEdit } from 'react-icons/fa';
import Modal from 'react-modal'; // Import du modal pour la modification
import '../../App.css';

const ModifierUG = () => {
    const [UGs, setUGs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false); // État pour ouvrir/fermer la modale
    const [selectedUG, setSelectedUG] = useState(null); // UG sélectionnée pour modification
    const [ugName, setUgName] = useState(''); // Nom de l'UG
    const [ugDescription, setUgDescription] = useState(''); // Description de l'UG
    const [isSubmitting, setIsSubmitting] = useState(false); // État pour le bouton de soumission

    // Fonction pour récupérer la liste des UG
    const fetchUG = async () => {
        try {
            const response = await PrivateAxios.get('/admin/ug');
            setUGs(response.data.ug);
        } catch (err) {
            const errorMsg = err.response ? err.response.data.message : 'Erreur lors du chargement des UG.';
            setError(errorMsg);
            toast.error(errorMsg, {
                position: "top-right",
                className: "custom-toast"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUG();
    }, []);

    // Fonction pour ouvrir la modale et pré-remplir les données de l'UG à modifier
    const openEditModal = (ug) => {
        setSelectedUG(ug);
        setUgName(ug.nom);
        setUgDescription(ug.description);
        setIsModalOpen(true);
    };

    // Fonction pour fermer la modale
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUG(null);
        setUgName('');
        setUgDescription('');
        setIsSubmitting(false);
    };

    // Fonction pour modifier une UG
    const updateUG = async () => {
        if (!selectedUG) return;

        setIsSubmitting(true);

        const payload = {
            nom: ugName,
            description: ugDescription,
        };

        try {
            await PrivateAxios.put(`/admin/ug/${selectedUG.id}`, payload);
            toast.success('UG modifiée avec succès !');
            fetchUG(); // Rafraîchir la liste des UG
            closeModal(); // Fermer la modale après succès
        } catch (error) {
            toast.error('Erreur lors de la modification de l\'UG.');
            setIsSubmitting(false);
        }
    };

    // Fonction pour supprimer une UG
    const deleteUG = async (ugId) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette UG ?')) return;

        try {
            await PrivateAxios.delete(`/admin/ug/${ugId}`);
            toast.success('UG supprimée avec succès !');
            fetchUG(); // Rafraîchir la liste après suppression
        } catch (error) {
            toast.error('Erreur lors de la suppression de l\'UG.');
        }
    };

    const filteredUGs = UGs.filter(ug =>
        ug.nom.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <h1 className="text-black text-3xl font-bold mb-6 text-center">Gestion des UG</h1>

            {/* Champ de recherche */}
            <div className="flex justify-center mb-4">
                <input
                    type="text"
                    placeholder="Rechercher par nom d'UG"
                    className="w-1/3 p-2 border border-gray-300 rounded"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loading ? (
                <p className="text-center">Chargement des UG existantes...</p>
            ) : error ? (
                <p className="text-red-500 text-center">{error}</p>
            ) : (
                <div className="flex justify-center">
                    <div className="overflow-x-auto w-full mx-auto text-center">
                        <table className="w-full bg-white border border-gray-200 shadow-md rounded-lg">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-2 py-2 border">ID</th>
                                    <th className="px-2 py-2 border">Nom</th>
                                    <th className="px-2 py-2 border">Description</th>
                                    <th className="px-2 py-2 border">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUGs.length > 0 ? (
                                    filteredUGs.map(ug => (
                                        <tr key={ug.id} className="hover:bg-gray-50">
                                            <td className="px-2 py-2 border">{ug.id}</td>
                                            <td className="px-2 py-2 border">{ug.nom}</td>
                                            <td className="px-2 py-2 border">{ug.description}</td>
                                            <td className="px-2 py-2 border">
                                                {/* Bouton Modifier */}
                                                <button
                                                    onClick={() => openEditModal(ug)}
                                                    className="text-yellow-500 hover:text-yellow-700 mx-2"
                                                    title="Modifier"
                                                >
                                                    <FaEdit />
                                                </button>

                                                {/* Bouton Supprimer */}
                                                <button
                                                    onClick={() => deleteUG(ug.id)}
                                                    className="text-red-500 hover:text-red-700 mx-2"
                                                    title="Supprimer"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4">Aucune UG trouvée.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modale pour modifier l'UG */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Modifier UG"
                ariaHideApp={false}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '400px',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                <h2 className="text-lg font-bold mb-4">Modifier UG</h2>
                <form>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ugName">
                            Nom de l'UG
                        </label>
                        <input
                            id="ugName"
                            type="text"
                            value={ugName}
                            onChange={(e) => setUgName(e.target.value)}
                            placeholder="Entrez le nom de l'UG"
                            required
                            className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ugDescription">
                            Description de l'UG
                        </label>
                        <textarea
                            id="ugDescription"
                            value={ugDescription}
                            onChange={(e) => setUgDescription(e.target.value)}
                            placeholder="Entrez une description"
                            required
                            className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                            rows="4"
                        />
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="px-4 py-2 bg-gray-300 rounded"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={updateUG}
                            className={`px-4 py-2 bg-blue-600 text-white rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'En cours...' : 'Valider'}
                        </button>
                    </div>
                </form>
            </Modal>
        </Layout>
    );
};

export default ModifierUG;
